import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { StatusBadge, TurnaroundBadge, isOverdue } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { REVIEW_STATUS } from "@/lib/constants";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CommentsPanel } from "@/components/CommentsPanel";
import { ConsultQueue } from "@/components/ConsultQueue";
import { AssignSelect, ReassignButton } from "@/components/ProsManager";

const EDITABLE = ["submitted", "in_review", "changes_suggested", "certified"];

export function ReviewDialog({ review, onClose, onSaved, base = "/admin/reviews" }) {
  const [f, setF] = useState({ status: review.status, editor_notes: review.editor_notes || "", revised_content: review.revised_content || review.submitted_content || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`${base}/${review.review_id}`, f);
      toast.success("Review updated");
      onSaved(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="admin-review-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{review.proposal_title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Client: {review.user_email}</p>
        <div className="grid gap-4 mt-2">
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="admin-review-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{EDITABLE.map((s) => <SelectItem key={s} value={s}>{REVIEW_STATUS[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Editor notes to client</Label><Textarea rows={4} className="mt-1.5" value={f.editor_notes} onChange={(e) => setF({ ...f, editor_notes: e.target.value })} data-testid="admin-review-notes-input" /></div>
          <div><Label>Revised proposal (Markdown)</Label><Textarea rows={14} className="mt-1.5 font-mono text-xs" value={f.revised_content} onChange={(e) => setF({ ...f, revised_content: e.target.value })} data-testid="admin-review-content-input" /></div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div id="review-doc" className="md:col-span-3 prose-gv border border-slate-200 rounded-lg p-5 max-h-[420px] overflow-y-auto text-sm" data-testid="admin-review-submitted-preview">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{review.submitted_content || ""}</ReactMarkdown>
            </div>
            <div className="md:col-span-2"><CommentsPanel proposalId={review.proposal_id} containerId="review-doc" /></div>
          </div>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-review-save-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save review</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { if (user?.role === "admin") api.get("/admin/reviews").then((r) => setItems(r.data)); }, [user]);
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  const onSaved = (r) => { setItems((xs) => xs.map((x) => (x.review_id === r.review_id ? r : x))); setOpen(null); };
  return (
    <div>
      <PageHeader eyebrow="Peer Verify editors" title="Editor review queue">Paid human review requests from all users.</PageHeader>
      {!items ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : items.length === 0 ? <p className="text-slate-500" data-testid="admin-queue-empty">No paid reviews in the queue.</p> : (
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-200">
          {items.map((r) => (
            <div role="button" key={r.review_id} onClick={() => setOpen(r)} data-testid={`admin-review-row-${r.review_id}`} className="w-full text-left p-5 flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 truncate">{r.proposal_title}</div><div className="text-xs text-slate-500">{r.user_email} · {r.submitted_at || r.created_at ? new Date(r.submitted_at || r.created_at).toLocaleString() : "—"}</div></div>
              <TurnaroundBadge r={r} testid={`admin-review-turnaround-${r.review_id}`} />
              {isOverdue(r) && <ReassignButton review={r} onAssigned={(n) => setItems((xs) => xs.map((x) => (x.review_id === n.review_id ? n : x)))} />}
              <span onClick={(e) => e.stopPropagation()}><AssignSelect kind="review" item={r} proType="editor" onAssigned={(n) => setItems((xs) => xs.map((x) => (x.review_id === n.review_id ? n : x)))} /></span>
              <StatusBadge status={r.status} map={REVIEW_STATUS} />
            </div>
          ))}
        </div>
      )}
      <ConsultQueue />
      {open && <ReviewDialog review={open} onClose={() => setOpen(null)} onSaved={onSaved} />}
    </div>
  );
}
